<template>
  <div class="stats-builder-chart">
    <CardApiError v-if="showRetry" @retry="setChartData" />
    <Card v-else v-loading="loading">
      <div v-if="!loading">
        <ChartWithTimeline :options="chartOptions" :events="events" dir="ltr" />
        <StatsChartHelper
          :primary-type="primaryType"
          :secondary-type="secondaryType"
          :announcement="announcement"
        />
      </div>
      <ElTooltip
        placement="top"
        :content="$t('APP.STATS_BUILDER.SAME_SCALE.DISABLE_REASON_TOOLTIP')"
        :disabled="shouldShowSameScale"
      >
        <ElSwitch
          v-model="sameScale"
          :disabled="!shouldShowSameScale"
          :active-text="$t('APP.STATS_BUILDER.SAME_SCALE')"
        />
      </ElTooltip>
    </Card>
  </div>
</template>

<script>
import chart from '@/mixins/chart';
import Card from '@@/common/Card';
import StatsChartHelper from '../StatsChartHelper';
import CardApiError from '@@/common/CardApiError';
import packageName from '@/mixins/packageName';
import { APP_DETAILS, APP_CHARTS_CONFIG } from '@/store/types';
import { Insight } from '@/api/api';
import ChartWithTimeline from '../ChartWithTimeline';
import {
  LAST_MONTH,
  LAST_WEEK,
  LAST_THREE_MONTH,
  LAST_SIX_MONTH,
} from '@/constant';
import transformChart from './chartTransformers';
import { deepMerge } from '@/utils/object';
import { mapGetters } from 'vuex';

function isObjectsValuesEqual(first = {}, second = {}) {
  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);
  if (firstKeys.length !== secondKeys.length) return false;
  return Object.keys(first).every(key => first[key] === second[key]);
}

export default {
  name: 'StatsBuilderChart',
  components: {
    Card,
    StatsChartHelper,
    CardApiError,
    ChartWithTimeline,
  },
  mixins: [chart, packageName],
  props: {
    primaryType: {
      type: Object,
      required: true,
    },
    secondaryType: {
      type: Object,
      default: () => ({}),
    },
    range: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      series: [],
      dates: [],
      options: {},
      timelineSeries: {},
      loading: true,
      showRetry: false,
      events: [],
      announcement: {},
      sameScale: false,
    };
  },
  computed: {
    ...mapGetters({
      app: APP_DETAILS,
      chartsConfig: APP_CHARTS_CONFIG,
    }),

    yAxisLabel() {
      const { child } = this.primaryType;
      const chart = this.chartsConfig[child];
      return chart ? chart.yAxis.title : '';
    },

    xAxisTickInterval() {
      switch (this.range) {
        case LAST_SIX_MONTH:
          return 30;
        case LAST_MONTH:
        case LAST_THREE_MONTH:
          return 7;
        case LAST_WEEK:
          return 1;
        default:
          return 1;
      }
    },
    chartOptions() {
      const options = {
        chart: {
          type: 'line',
        },
        xAxis: {
          categories: this.dates,
          tickInterval: this.xAxisTickInterval,
        },
        yAxis: [
          {
            title: {
              text: this.yAxisLabel,
            },
          },
        ],
        series: this.series,
        ...this.options,
      };
      return options;
    },
    shouldShowSameScale() {
      return !!this.secondaryType.name;
    },
  },
  watch: {
    primaryType(newValue, oldValue) {
      //TODO: the primaryType variable type should change from Object to String
      //in that way watcher called when it's actually changed and we will not need the if anymore
      if (isObjectsValuesEqual(oldValue, newValue)) return;
      this.setChartData();
    },
    secondaryType(newValue, oldValue) {
      //TODO: the secondaryType variable type should change from Object to String
      //in that way watcher called when it's actually changed and we will not need the if anymore
      if (isObjectsValuesEqual(oldValue, newValue)) return;
      this.setChartData();
    },
    range() {
      this.setChartData();
      this.getEvents();
    },
    sameScale() {
      this.setChartData();
    },
  },
  created() {
    this.setChartData();
    this.getEvents();
  },
  methods: {
    setChartData() {
      this.loading = true;
      this.showRetry = false;
      return Insight.getCharts({
        packageName: this.packageName,
        app: this.app,
        selectedFilters: {
          primaryType: this.primaryType,
          secondaryType: this.secondaryType,
          range: this.range,
        },
      })
        .then(({ charts, dates, options }) => {
          const { primaryChart, secondaryChart } = charts;
          const { series, options: transformedOptions } = transformChart({
            primaryChart,
            secondaryChart,
            chartsConfig: this.chartsConfig,
            sameScale: this.sameScale,
          });
          this.dates = dates;
          this.series = series;
          this.options = deepMerge(options, transformedOptions);

          const primaryAnnouncement = primaryChart.announcement;
          const secondaryAnnouncement =
            secondaryChart && secondaryChart.announcement;
          this.announcement = primaryAnnouncement || secondaryAnnouncement;
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
          this.showRetry = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getEvents() {
      return Insight.getEvents({
        packageName: this.packageName,
        range: this.range,
      })
        .then(events => {
          this.events = events;
        })
        .catch(() => {
          /* DO Nothing, just to prevent UnhandledRejection error */
        });
    },
  },
};
</script>

<style lang="scss">
.stats-builder-chart {
  .el-switch__label {
    margin-right: calc($global-margin / 2);

    * {
      font-size: 0.8rem;
    }
  }
}
</style>
